import { useGetUserNotificationsQuery } from 'store/services/core/user';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { NotificationsOutlined, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLogin, useStore } from 'contexts/index';
import { STORE_KEYS } from 'types/store';

import { invokeRNMessage, isMobileApp } from 'utils/common';
import { useNotificationRefresh } from 'utils/hooks';
import { NotificationItem } from './NotificationItem';
import { ReactNativeMessageType, type NotificationResponse } from 'types';
import { CustomBadge } from '../../CustomBadge';
import {
  FilterButton,
  FilterContainer,
  NoResults,
  NotificationIconButton,
  NotificationWrapper,
  SearchIconWrapper,
  SearchInput,
  SearchWrapper,
  StyledMenu,
} from './NotificationsCenter.styles';

interface NotificationsCenterProps {
  testId?: string;
}

export const NotificationsCenter = memo(({ testId = 'user-dashboard-notifications-button' }: NotificationsCenterProps) => {
  const { t } = useTranslation();
  const { user } = useLogin();
  const { getItem } = useStore();
  const [filter, setFilter] = useState<'All' | 'Unread'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data: notificationData, refetch } = useGetUserNotificationsQuery({ id: user?.user_id ?? '' }, { skip: !user?.user_id });

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useNotificationRefresh(refetch);

  const auth = getItem(STORE_KEYS.AUTH);

  const notifications = useMemo(() => notificationData?.items?.filter((item) => item.type === 'Web') ?? [], [notificationData]);

  const { filteredNotifications, unreadCount } = useMemo(() => {
    const unreadNotifications = notifications.filter((notification) => notification.readState === 'Unread');
    const byFilter = filter === 'Unread' ? unreadNotifications : notifications;
    const query = searchQuery.trim().toLowerCase();
    const bySearch = query
      ? byFilter.filter((n) => n.subject?.toLowerCase().includes(query) || n.bodyPlainText?.toLowerCase().includes(query))
      : byFilter;
    return {
      filteredNotifications: bySearch,
      unreadCount: unreadNotifications.length,
    };
  }, [filter, notifications, searchQuery]);

  const jwtToken = useMemo(() => auth?.jwtToken?.replace('Bearer ', ''), [auth]);

  useEffect(() => {
    if (isMobileApp()) invokeRNMessage(ReactNativeMessageType.NotificationCount, { count: unreadCount });
  }, [unreadCount]);

  const handleOnNotificationUpdateClick = useCallback(() => {
    refetch();
    window.dispatchEvent(new CustomEvent('flowio:refresh-notifications'));
  }, [refetch]);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
    setFilter('All');
  }, []);

  useEffect(() => {
    if (!jwtToken || !user) return;

    const newConnection = new HubConnectionBuilder()
      //for local development use .withUrl("https://localhost:7231/api/core/ws/notification", {
      .withUrl('/api/core/ws/notification', {
        accessTokenFactory: () => jwtToken,
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    newConnection.on('ReceiveNotification', (message: NotificationResponse) => {
      refetchRef.current();
      newConnection.invoke('NotificationReceived', user.user_id, message.id).catch(() => {
        /* ignore invoke errors on disconnected connection */
      });
    });

    newConnection.on('ReloadNotifications', () => {
      refetchRef.current();
    });

    newConnection.onclose(() => {
      /* Swallow disconnect errors to prevent unhandled rejections */
    });

    const start = async () => {
      if (newConnection.state === HubConnectionState.Disconnected) {
        try {
          await newConnection.start();
        } catch {
          /* SignalR not available — polling/event fallback handles refresh */
        }
      }
    };
    start();

    let stopping = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopping = true;
        newConnection
          .stop()
          .catch(() => {
            /* ignore stop errors */
          })
          .finally(() => {
            stopping = false;
            if (!document.hidden) {
              start();
            }
          });
      } else if (!stopping) {
        start();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      newConnection.stop().catch(() => {
        /* ignore stop errors */
      });
    };
  }, [jwtToken, user]);

  return (
    <>
      <NotificationWrapper>
        <NotificationIconButton
          data-testid={testId}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            refetch();
          }}>
          <NotificationsOutlined sx={{ color: 'text.secondary', fontSize: 22 }} />
        </NotificationIconButton>
        {unreadCount > 0 && (
          <CustomBadge
            data-testid="user-dashboard-notifications-badge"
            sx={{ position: 'absolute', top: -6, right: -10, ml: 0, padding: '2px 6px', fontSize: '12px' }}>
            {unreadCount}
          </CustomBadge>
        )}
      </NotificationWrapper>
      <StyledMenu
        anchorEl={anchorEl}
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        elevation={0}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        <SearchWrapper>
          <SearchIconWrapper>
            <Search sx={{ fontSize: 18 }} />
          </SearchIconWrapper>
          <SearchInput
            placeholder={t('common.search._')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="notifications-search-input"
          />
        </SearchWrapper>
        <FilterContainer>
          <FilterButton active={filter === 'All'} onClick={() => setFilter('All')}>
            {t('notification.all')}
          </FilterButton>
          <FilterButton active={filter === 'Unread'} onClick={() => setFilter('Unread')}>
            {t('notification.unread')}
          </FilterButton>
        </FilterContainer>
        {filteredNotifications.length === 0 ? (
          <NoResults>{t('search.noResults')}</NoResults>
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              {...notification}
              onNotificationUpdateClick={handleOnNotificationUpdateClick}
              closeMenu={handleCloseMenu}
            />
          ))
        )}
      </StyledMenu>
    </>
  );
});
